import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiParameters from '../../../../enum/DefaultApiParameters';
import {
  OcppLogsProperties,
  OcppLogsRequest,
} from '../../../../interface/device-ocpp-logs.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchOcppLogs,
  fetchOcppLogsFilters,
  updateOcppLogsFilters,
} from '../../../../redux/slices/DeviceOcppLogsSilce';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import capitalizeFirstLetter from '../../../../shared-functions/StringFunctions';

/**
 * Component to disable charging schedule based on charging point
 * @returns
 */
function OcppLogs() {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const { ocppLogs, limit, page, loading, totalPages, totalResults, filterListLoading, filters } =
    useAppSelector((s) => s.ocppLogs);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);

  const column = [
    {
      label: t('uniqueId'),
      accessorKey: 'uniqueId' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('date'),
      accessorKey: 'date' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 40,
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 40,
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 20,
      /**
       * Display type in ocpp table
       * @param row
       * @returns
       */
      customData: (row: OcppLogsProperties) => (
        <div className={`ocpp-logs-type ${row?.type}`}>
          <p>{capitalizeFirstLetter(row?.type)}</p>
        </div>
      ),
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('heartbeatCount'),
      accessorKey: 'heartbeatcount' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof OcppLogsProperties,
      sortAllow: false,
    },
  ];

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: OcppLogsRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchOcppLogs({
        ...value,
        ...queryParameters,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param logId
   */
  const handleOpenSidePanelOnRowClick = (logId: string) => {
    // Set or update the logId param
    searchParameters.set('logId', logId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchOcppLogs({
        page: DefaultApiParameters.page,
        limit: DefaultApiParameters.limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
        ...queryParameters,
      }),
    );
  }, [filters, selectedChargingPoint]);

  return (
    <div className="device-charging-schedules-wrap">
      <div className="device-charging-schedules-table">
        <CustomTable
          header={column}
          data={ocppLogs}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="logId"
          rowClickSidePanel={<OcppLogSidePanel fetchIdFrom="queryParams" />}
          filters={filters}
          filtersToDispatch={updateOcppLogsFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchOcppLogsFilters}
          version="v2"
        />
      </div>
    </div>
  );
}

export default OcppLogs;
